import { useEffect, useRef, useState } from "react";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PhoneCall, Check } from "lucide-react";
import { QuoteBuilderModal } from "@/components/planner/admin/QuoteBuilderModal";
import { TemplateEditor } from "@/components/planner/admin/TemplateEditor";
import { QuoteStatusPill } from "@/components/planner/QuoteStatusPill";

const TABS = [["overview", "Overview"], ["quotes", "Quotation requests"], ["templates", "Checklists"], ["callbacks", "Callbacks"]];

export default function PlannerAdminPage() {
    const [tab, setTab] = useState("overview");
    return (
        <div data-testid="planner-admin-page">
            <h1 className="text-3xl hdb-heading font-black text-slate-900">Essentials Planner</h1>
            <p className="text-slate-500 mt-1 text-sm">Home &amp; business lists, quotation requests and starter checklists</p>
            <div className="flex gap-1 border-b border-slate-200 mt-4 mb-5">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`planner-admin-tab-${k}`}
                            className={`px-4 py-2 text-sm font-bold -mb-px border-b-2 transition-colors ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>{l}</button>
                ))}
            </div>
            {tab === "overview" && <Overview/>}
            {tab === "quotes" && <Quotes/>}
            {tab === "templates" && <TemplateEditor/>}
            {tab === "callbacks" && <Callbacks/>}
        </div>
    );
}

const Stat = ({ label, value, tid }) => (
    <div className="hdb-card px-4 py-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className="hdb-num text-2xl font-black text-slate-900" data-testid={tid}>{value ?? 0}</div></div>
);

function Overview() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/planner/stats").then((r) => setS(r.data)); }, []);
    if (!s) return <Loader2 className="animate-spin text-slate-400"/>;
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="planner-admin-stats">
            <Stat label="Home planners started" value={s.profiles.HOME} tid="planner-stat-home"/>
            <Stat label="Business planners started" value={s.profiles.BUSINESS} tid="planner-stat-business"/>
            <Stat label="Draft lists" value={s.drafts} tid="planner-stat-drafts"/>
            <Stat label="Inactive drafts (7+ days)" value={s.inactive_drafts} tid="planner-stat-inactive"/>
            <Stat label="Quotes to prepare" value={s.quotes.requested} tid="planner-stat-requested"/>
            <Stat label="Quotes awaiting customer" value={s.quotes.quoted} tid="planner-stat-quoted"/>
            <Stat label="Accepted orders" value={s.quotes.accepted} tid="planner-stat-accepted"/>
            <Stat label="Unresolved items in open quotes" value={s.unresolved_lines} tid="planner-stat-unresolved"/>
            <Stat label="Repeat customers" value={s.repeat_clients} tid="planner-stat-repeat"/>
            <Stat label="Open callback requests" value={s.open_callbacks} tid="planner-stat-callbacks"/>
            <Stat label="Lists saved (events)" value={s.events.list_saved} tid="planner-stat-saved"/>
            <Stat label="Guest drafts" value={s.guest_drafts} tid="planner-stat-guest"/>
        </div>
    );
}

function Quotes() {
    const [status, setStatus] = useState("requested");
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(null);
    const reqId = useRef(0);
    const load = () => {
        const id = ++reqId.current;
        return api.get("/admin/planner/quotes", { params: { status } }).then((r) => id === reqId.current && setRows(r.data));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setRows(null); load(); }, [status]);
    return (
        <div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="hdb-input !w-52 mb-3" data-testid="planner-admin-quote-filter">
                <option value="requested">To prepare</option><option value="quoted">Sent to customer</option>
                <option value="accepted">Accepted</option><option value="declined">Declined</option><option value="">All</option>
            </select>
            <div className="hdb-card overflow-hidden">
                {!rows ? <div className="p-8 text-center"><Loader2 className="inline animate-spin text-slate-400"/></div> : rows.length === 0 ? (
                    <div className="p-8 text-center text-slate-400" data-testid="planner-admin-quotes-empty">Nothing here.</div>
                ) : (
                    <table className="hdb-table w-full text-sm">
                        <thead><tr><th className="text-left">No.</th><th className="text-left">Customer</th><th className="text-left">Type</th>
                            <th className="text-right">Items</th><th className="text-right">To confirm</th><th className="text-right">Estimate / Quote</th><th className="text-left">Status</th><th/></tr></thead>
                        <tbody>{rows.map((q, i) => (
                            <tr key={q.id} data-testid={`planner-admin-quote-${i}`}>
                                <td className="hdb-mono">{q.number}</td>
                                <td><div className="font-semibold">{q.client_name}</div><div className="text-xs text-slate-500">{q.client_phone}</div></td>
                                <td className="text-xs">{q.planner_type}{q.first_order_eligible && <span className="ml-1 text-emerald-700 font-bold">· 1st order</span>}</td>
                                <td className="hdb-num hdb-num-right">{q.snapshot.lines.length}</td>
                                <td className="hdb-num hdb-num-right">{q.unresolved_count}</td>
                                <td className="hdb-num hdb-num-right">{fmt(q.quote?.total ?? q.snapshot.estimated_total)}</td>
                                <td><QuoteStatusPill status={q.status}/></td>
                                <td className="text-right"><button className="hdb-btn hdb-btn-ghost text-xs" onClick={() => setOpen(q)} data-testid={`planner-admin-quote-open-${i}`}>
                                    {["requested", "quoted"].includes(q.status) ? "Prepare quote" : "View"}</button></td>
                            </tr>))}</tbody>
                    </table>
                )}
            </div>
            {open && <QuoteBuilderModal q={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }}/>}
        </div>
    );
}

function Callbacks() {
    const [rows, setRows] = useState([]);
    const load = () => api.get("/admin/planner/callbacks").then((r) => setRows(r.data));
    useEffect(() => { load(); }, []);
    const done = async (c) => { await api.post(`/admin/planner/callbacks/${c.id}/done`); toast.success("Marked done"); load(); };
    return (
        <div className="hdb-card divide-y divide-slate-100" data-testid="planner-admin-callbacks">
            {rows.length === 0 && <div className="p-8 text-center text-slate-400">No callback requests.</div>}
            {rows.map((c, i) => (
                <div key={c.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`planner-callback-${i}`}>
                    <PhoneCall size={14} className="text-slate-400"/>
                    <div className="flex-1"><b>{c.name || "—"}</b> · <a href={`tel:${c.phone}`} className="hdb-mono text-blue-700">{c.phone}</a>
                        <span className="text-xs text-slate-500 ml-2">{c.planner_type} · {c.created_at.slice(0, 16).replace("T", " ")}</span></div>
                    {c.status === "open" ? <button className="hdb-btn hdb-btn-ghost text-xs" onClick={() => done(c)} data-testid={`planner-callback-done-${i}`}><Check size={11}/> Done</button>
                        : <span className="text-xs text-emerald-700 font-bold">Done</span>}
                </div>
            ))}
        </div>
    );
}
